/* @flow */

import i18next, { createInstance as createI18nextInstance } from 'i18next';
import type { InitOptions } from 'i18next';
import { when } from 'mobx';
import { flatten, unflatten } from 'flat';

import type UserStore from 'stores/UserStore';

import resourcesGeneralEn from 'i18n/general/en.json';
import resourcesGeneralFrRaw from 'i18n/general/fr.json';
import resourcesGeneralEsRaw from 'i18n/general/es.json';
import resourcesGeneralItRaw from 'i18n/general/it.json';
import resourcesGeneralDeRaw from 'i18n/general/de.json';
import resourcesGeneralNlRaw from 'i18n/general/nl.json';

const generalEnFlat = flatten(resourcesGeneralEn);
const generalFrRawFlat = flatten(resourcesGeneralFrRaw);
const generalEsRawFlat = flatten(resourcesGeneralEsRaw);
const generalItRawFlat = flatten(resourcesGeneralItRaw);
const generalDeRawFlat = flatten(resourcesGeneralDeRaw);
const generalNlRawFlat = flatten(resourcesGeneralNlRaw);

function hydrateFlatWithFallback(input: { [string]: string }, fallback: { [string]: string }) {
  return Object.entries(fallback).reduce((result, [fallbackKey, fallbackValue]) => {
    const _result = result;
    _result[fallbackKey] = input[fallbackKey] || fallbackValue;

    return _result;
  }, {});
}

const generalFrFlat = hydrateFlatWithFallback(generalFrRawFlat, generalEnFlat);
const resourcesGeneralFr = unflatten(generalFrFlat, { object: true });

const generalEsFlat = hydrateFlatWithFallback(generalEsRawFlat, generalEnFlat);
const resourcesGeneralEs = unflatten(generalEsFlat, { object: true });

const generalItFlat = hydrateFlatWithFallback(generalItRawFlat, generalEnFlat);
const resourcesGeneralIt = unflatten(generalItFlat, { object: true });

const generalDeFlat = hydrateFlatWithFallback(generalDeRawFlat, generalEnFlat);
const resourcesGeneralDe = unflatten(generalDeFlat, { object: true });

const generalNlFlat = hydrateFlatWithFallback(generalNlRawFlat, generalEnFlat);
const resourcesGeneralNl = unflatten(generalNlFlat, { object: true });

export const GLOBAL_FALLBACK_LANG = 'en';

export type AvailableLanguages = 'en' | 'fr' | 'es' | 'it' | 'de' | 'nl';

type TypeResourceBundle = {|
  namespace: string,
  language: AvailableLanguages,
  resources: { [key: string]: string },
|};

const resourceBundles: TypeResourceBundle[] = [
  { namespace: 'general', language: 'en', resources: resourcesGeneralEn },
  { namespace: 'general', language: 'fr', resources: resourcesGeneralFr },
  { namespace: 'general', language: 'es', resources: resourcesGeneralEs },
  { namespace: 'general', language: 'it', resources: resourcesGeneralIt },
  { namespace: 'general', language: 'de', resources: resourcesGeneralDe },
  { namespace: 'general', language: 'nl', resources: resourcesGeneralNl },
];

let resolver: Function;

let i18nInstance: Promise<typeof i18next> | typeof i18next = new Promise(resolve => {
  resolver = resolve;
});

/**
 * When using the <Trans> component provided by react-i18next, interpolated parameters must be passed in
 * object literals.  However, Flow complains about this, because React isn't designed to accept object literals
 * in this way.  (The reason why it works is that the <Trans> component converts the object literals into
 * valid React code.)  To suppress the Flow errors, this wrapper function returns a weak type of "any".
 *
 * @see react.i18next.com/components/trans-component.html
 * @see github.com/i18next/react-i18next/blob/v7.3.6/src/Trans.js
 */
export function transParam(param: { [key: string]: string }) {
  return param;
}

export const resolveLanguage = async (userStore: UserStore) => {
  await when(() => !!userStore.userLanguage);

  return userStore.userLanguage;
};

export default async function createI18n(userStore: UserStore): Promise<typeof i18next> {
  const userLanguage = await resolveLanguage(userStore);

  const options: InitOptions = {
    whitelist: ['en', 'fr', 'es', 'it', 'de', 'nl'],
    lng: userLanguage || GLOBAL_FALLBACK_LANG,
    fallbackLng: GLOBAL_FALLBACK_LANG,
    defaultNS: 'general',
    returnEmptyString: false,
    interpolation: {
      format(value, format) {
        if (format === 'lowercase') {
          return value.toLowerCase();
        }

        return value;
      },
    },
  };

  const i18nextInstance = createI18nextInstance();

  await new Promise(resolve => {
    i18nextInstance.init(options, () => {
      for (const resourceBundle of resourceBundles) {
        i18nextInstance.addResourceBundle(
          resourceBundle.language,
          resourceBundle.namespace,
          resourceBundle.resources
        );
      }

      resolve();
    });
  });

  resolver(i18nextInstance);
  i18nInstance = i18nextInstance;

  return i18nInstance;
}

export const getI18nInstance = (): Promise<typeof i18next> =>
  i18nInstance instanceof Promise ? i18nInstance : Promise.resolve(i18nInstance);
